import { httpApi } from '@app/api/http.api';
import { podcastImages } from '@app/assets/images/podcasts';
import { DealRoomSection } from '@app/constants/enums/deal-room';

export interface IPodcast {
  id: string;
  title: string;
  description?: string;
  /** Episode sub-header — usually the guest/speaker line, shown as a badge above the title. */
  tag?: string;
  /** Artwork filename bundled with the client, e.g. "episode-04.webp". */
  image?: string;
  videoUrl?: string;
  podcastLink: string;
  types: string[];
  order?: number;
}

interface PodcastListResponse {
  podcasts: IPodcast[];
}

const baseSlug = '/podcasts';

export type PodcastType =
  | DealRoomSection.PODCAST_INSURANCE
  | DealRoomSection.PODCAST_LEGAL
  | DealRoomSection.PODCAST_MANAGEMENT_APP
  | DealRoomSection.PODCAST_FIRE_SAFETY
  | DealRoomSection.PODCAST_MANAGEMENT_COMPANIES
  | DealRoomSection.PODCAST_MAINTENANCE
  | DealRoomSection.PODCAST_NEW_BUILDINGS_FORM_4;

/**
 * Resolves the artwork name stored on the record to a bundled asset.
 *
 * The API only carries the image *name* — the bytes ship with the client (see
 * assets/images/podcasts), so the list response stays small and the browser
 * serves the artwork from its static asset cache. Returns undefined for an
 * unknown or missing name, letting the caller fall back to the shared image.
 */
export const getPodcastImageSrc = (podcast: IPodcast): string | undefined =>
  podcast.image ? podcastImages[podcast.image] : undefined;

export const getPodcastsByTypeAuth = async (type: PodcastType): Promise<IPodcast[]> => {
  try {
    const { data } = await httpApi.get<PodcastListResponse>(`${baseSlug}/by-type/${encodeURIComponent(type)}`);
    return data.podcasts;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getPodcastsByTypePublic = async (
  dealRoomId: string,
  type: PodcastType,
  credentials: { sessionId?: string | null; uniqueId?: string | null },
): Promise<IPodcast[]> => {
  try {
    const params = new URLSearchParams();
    if (credentials.sessionId) params.set('sessionId', credentials.sessionId);
    if (credentials.uniqueId) params.set('uniqueId', credentials.uniqueId);
    const qs = params.toString();
    const url = `${baseSlug}/public/${dealRoomId}/by-type/${encodeURIComponent(type)}${qs ? `?${qs}` : ''}`;
    const { data } = await httpApi.get<PodcastListResponse>(url);
    return data.podcasts;
  } catch (error) {
    return await Promise.reject(error);
  }
};
